import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { resolveBrand } from '@/config/brand-resolver'
import { type BrandConfig, type BrandId, brands, type CircleTheme } from '@/config/brands'

interface BrandContextValue {
  brand: BrandConfig
  brandId: BrandId
  circleTheme: CircleTheme
  setBrandId: (id: BrandId) => void
  setCircleTheme: (theme: CircleTheme) => void
}

const BrandContext = createContext<BrandContextValue | null>(null)

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [brandId, setBrandId] = useState<BrandId>(() => resolveBrand())
  const brand = brands[brandId]
  const [circleTheme, setCircleTheme] = useState<CircleTheme>('pink')

  useEffect(() => {
    const html = document.documentElement
    html.classList.remove('brand-gg', 'brand-circle', 'brand-everie', 'theme-pink', 'theme-blue')
    html.classList.add(`brand-${brandId}`)
    if (brandId === 'circle') {
      html.classList.add(`theme-${circleTheme}`)
    }
  }, [brandId, circleTheme])

  const value = useMemo(
    () => ({ brand, brandId, circleTheme, setBrandId, setCircleTheme }),
    [brand, brandId, circleTheme],
  )

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
}

export function useBrand(): BrandContextValue {
  const context = useContext(BrandContext)
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider')
  }
  return context
}
